import React, { useContext, useState } from 'react';
import styled from 'astroturf/react';
import { CiterightContext } from '../CiterightContextProvider';
import { TriagePage } from '../TriagePage';
import { ClaimPage } from '../ClaimPage';
import { OverviewPage } from '../OverviewPage';
import { DebugPage } from '../DebugPage';
import { DetailsPage } from '../DetailsPage';
import { TopBar } from './TopBar';
import { ClaimFilter } from './ClaimFilterPanel';
import { AVAILABLE_SOURCES, FilterSettings } from './FilterPanel';
import { SortSettings } from './SortPanel';
import { useOverviewReadyToast } from '../hooks/useOverviewReadyToast';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--spacing-xs-8) var(--spacing-sm-24) var(--spacing-lg-80)
    var(--spacing-sm-24);
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-24);
`;

export const SimpleResultsView: React.FC = () => {
  const [page, setPage] = useState<'triage' | 'debug' | 'overview'>('triage');
  const [claimFilter, setClaimFilter] = useState<ClaimFilter>('all');
  const [filterSettings, setFilterSettings] = useState<FilterSettings>({
    relevance: { min: 0 },
    journalImpactFactor: { min: 0 },
    citations: { min: 0 },
    sources: { selected: AVAILABLE_SOURCES.map((s) => s.value) },
    excludePreprints: { enabled: false },
  });
  const [sortSettings, setSortSettings] = useState<SortSettings | null>(null);

  const context = useContext(CiterightContext);
  const activeClaimId = context?.activeClaimId;
  const setActiveClaimId = context?.setActiveClaimId;
  const activeWorkId = context?.activeWorkId;

  // Show toast when overview is ready
  useOverviewReadyToast(setPage);

  // Filter claims based on current filter setting
  const claims = context?.report?.claims || [];
  const filteredClaims = claims.filter((claim) => {
    if (claimFilter === 'all') return true;
    if (claimFilter === 'essential') return claim.priority === 'High';
    if (claimFilter === 'optional') return claim.priority === 'Low';
    return true;
  });

  const currentIndex = activeClaimId
    ? filteredClaims.findIndex((c) => c.id === activeClaimId)
    : -1;

  const goNext =
    currentIndex >= 0 &&
    currentIndex < filteredClaims.length - 1 &&
    setActiveClaimId
      ? () => {
          setActiveClaimId(filteredClaims[currentIndex + 1].id);
        }
      : null;

  const goPrevious =
    currentIndex > 0 && setActiveClaimId
      ? () => {
          setActiveClaimId(filteredClaims[currentIndex - 1].id);
        }
      : null;

  return (
    <>
      <TopBar
        page={page}
        onToggle={setPage}
        onNavigateToOverview={() => setPage('overview')}
      />
      <Container>
        <ContentWrapper>
          {page === 'debug' ? (
            <DebugPage />
          ) : page === 'overview' ? (
            <OverviewPage
              onClaimSelect={(claimId) => {
                setActiveClaimId?.(claimId);
                setPage('triage');
              }}
              onSwitchToTriage={() => setPage('triage')}
            />
          ) : activeWorkId ? (
            <DetailsPage />
          ) : activeClaimId ? (
            <ClaimPage
              claimId={activeClaimId}
              onBack={() => setActiveClaimId?.(null)}
              onNext={goNext}
              onPrevious={goPrevious}
              claimIndex={currentIndex}
              totalClaims={filteredClaims.length}
              filterSettings={filterSettings}
              sortSettings={sortSettings}
              onFilterChange={setFilterSettings}
              onSortChange={setSortSettings}
            />
          ) : (
            <TriagePage
              onClaimSelect={setActiveClaimId || (() => {})}
              claimFilter={claimFilter}
              onClaimFilterChange={setClaimFilter}
              showBackToOverview={
                context?.report?.done && context?.report?.overview_ready
              }
              onBackToOverview={() => setPage('overview')}
            />
          )}
        </ContentWrapper>
      </Container>
    </>
  );
};

export default SimpleResultsView;
